/**
 * @fileoverview A module for creating an API instance with authentication and error handling.
 *
 * @example
 * const api = createAPI()
 * api.get('/participants')
 */

import axios, { AxiosError, AxiosResponse } from 'axios'
import { DateTime } from 'luxon'
import { v4 as uuid } from 'uuid'
import config from './config'
import { changePassword } from './utils/changePassword'
import { logout } from './utils/logout'

/**
 * @function isExpired
 * @description Checks if the response status is 403 (expired token).
 * @param {AxiosResponse} response - The response from the API.
 * @returns {boolean} - True if the response status is 403, false otherwise.
 */
function isExpired({ status }: AxiosResponse) {
    return status === 403
}

/**
 * @function isOldPassword
 * @description Checks if the response status is 406 (old password).
 * @param {AxiosResponse} response - The response from the API.
 * @returns {boolean} - True if the response status is 406, false otherwise.
 */
function isOldPassword({ status }: AxiosResponse) {
    return status === 406
}

/**
 * @function createAPI
 * @description Creates an API instance with authentication and error handling.
 * @returns {AxiosInstance} - The API instance.
 */
const createAPI = () => {
    const defaultOptions = {
        baseURL: config.api,
        headers: {
            'Content-Type': 'application/json',
            'X-Request-Id': uuid(),
        },
    }

    // Create instance
    const instance = axios.create(defaultOptions)

    // Set AUTH token for every request if available
    instance.interceptors.request.use(function (config) {
        const token = localStorage.getItem('token')
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
        config.headers['X-Request-Id'] = uuid()
        return config
    })

    instance.interceptors.response.use(
        (response) => {
            const { token } = response.headers
            if (token) {
                const payload = JSON.parse(atob(token.split('.')[1]))
                const exp = DateTime.fromSeconds(payload.exp)
                const iat = DateTime.fromSeconds(payload.iat)

                const expiringAt = DateTime.now()
                    .plus(iat.diffNow().plus(exp.diff(iat)))
                    .valueOf()

                localStorage.setItem('token', token)
                localStorage.setItem('expiringAt', expiringAt.toString())
            }
            return response
        },
        (error: AxiosError) => {
            if (isExpired(error.response)) {
                logout('timeout')
            } else if (isOldPassword(error.response)) {
                changePassword()
            } else {
                throw error
            }
        }
    )

    return instance
}

export const api = createAPI()
